/**
 * Azure image <-> VM size compatibility, for the cluster form.
 *
 * Line-for-line twin of the helpers in api/util/azureCatalog.js, so the form
 * greys out exactly what the deploy preflight would refuse. Keep the two in
 * step: a rule that lives in only one of them is a form that lets you save a
 * cluster the deploy then rejects, or the reverse.
 */

// Heidi's packer templates name Azure images `<prefix>-<kind>` with an
// OPTIONAL `-<epoch>` suffix - clusters/azure-unified/data.tf matches both
// `<prefix>-heidi` exactly and `<prefix>-heidi-<epoch>`.
const IMAGE_NAME_RE = /^(.+)-(heidi|server|node|gpu)(?:-(\d+))?$/;

export function parseImageName(name) {
    const match = IMAGE_NAME_RE.exec(String(name || ''));
    if (!match) return null;
    return { prefix: match[1], kind: match[2], timestamp: match[3] ? Number(match[3]) : null };
}

/** Is this image usable in `region`? Managed images are regional; gallery versions are replicated. */
export function imageInRegion(image, region) {
    if (!image || !region) return false;
    if (image.source === 'gallery') return (image.regions || []).includes(region);
    return image.region === region;
}

/**
 * The image terraform would pick for an image name: mirrors data.tf's rule -
 * `<name>-<kind>` or `<name>-<kind>-<epoch>`, lexicographically latest, then
 * the gallery definition of the same name.
 */
export function resolveStackImage(images, imageName, kind, region) {
    kind = kind || 'heidi';
    if (!imageName || !Array.isArray(images)) return null;
    const usable = images.filter((img) => !region || imageInRegion(img, region));

    const exact = `${imageName}-${kind}`;
    const managed = usable.filter(
        (img) => img.source === 'managed' && (img.name === exact || img.name.startsWith(`${exact}-`))
    );
    if (managed.length) return managed.reduce((best, img) => (img.name > best.name ? img : best));

    const gallery = usable.filter((img) => img.source === 'gallery' && img.prefix === imageName);
    if (gallery.length) return gallery.reduce((best, img) => (img.version > best.version ? img : best));
    return null;
}

const archLabel = (arch) => (arch === 'x86_64' ? 'x86' : arch);

/**
 * Can this image boot on this VM size? Returns { compatible: true } or
 * { compatible: false, code, shapeReason, imageReason } - shapeReason greys a
 * size, imageReason greys an image.
 */
export function imageShapeCompatibility(image, shape, options) {
    if (!image || !shape) return { compatible: true };
    const id = shape.instance_id;

    const terms = options && options.terms;
    if (terms && terms.accepted === false) {
        const product = (options && options.productName) || 'this Marketplace image';
        return {
            compatible: false,
            code: 'TERMS',
            shapeReason: `terms not accepted for ${product}`,
            imageReason: `terms not accepted for ${product}`,
        };
    }

    const shapeArchs = shape.archs && shape.archs.length ? shape.archs : [shape.arch || shape.architecture];
    if (image.arch && shapeArchs.length && !shapeArchs.includes(image.arch)) {
        const shapeArch = shape.arch || shape.architecture;
        return {
            compatible: false,
            code: 'ARCH',
            shapeReason: `${archLabel(shapeArch)} size, image is ${archLabel(image.arch)}`,
            imageReason: `${archLabel(image.arch)} image, ${id} is ${archLabel(shapeArch)}`,
        };
    }

    const generations = shape.hyperVGenerations || [];
    if (image.hyperVGeneration && generations.length && !generations.includes(image.hyperVGeneration)) {
        return {
            compatible: false,
            code: 'HYPERV',
            shapeReason: `needs ${generations.join('/')}, image is ${image.hyperVGeneration}`,
            imageReason: `${image.hyperVGeneration} image, ${id} needs ${generations.join('/')}`,
        };
    }

    return { compatible: true };
}

/**
 * Is there room in this family's vCPU quota for `needed` more cores?
 * null when unknown, so a missing quota never greys anything out.
 */
export function quotaShortfall(quota, shape, needed) {
    if (!quota || !shape || !shape.family || !shape.vcpus) return null;
    const entry = quota[shape.family];
    if (!entry || typeof entry.limit !== 'number') return null;
    const free = entry.limit - entry.current;
    if (needed <= free) return null;
    return { label: entry.label, current: entry.current, limit: entry.limit, needed, free };
}

/** "Standard DSv5 Family vCPUs: 20 of 60 free, this cluster needs 44" */
export function quotaReason(short) {
    if (!short) return null;
    return `${short.label}: ${short.free} of ${short.limit} free, this cluster needs ${short.needed}`;
}

/** A human label for one image row. */
export function describeImage(image) {
    if (!image) return '';
    const bits = [image.source === 'gallery' ? `gallery ${image.version}` : image.name];
    if (image.arch) bits.push(archLabel(image.arch));
    if (image.hyperVGeneration) bits.push(image.hyperVGeneration);
    return bits.join(' · ');
}
